import type { MetricsRow } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { DateRangeOperator, getDateRangeValues } from 'app/filter.utils'
import type { FilterableColumn } from 'app/filter.utils'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { defaultColumns, filterableColumns } from 'app/table-columns/companies/workspaces'
import { hasRole, setWorkspace, useUser } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { graphql } from 'app/generated/gql'
import { gqlFetchList, gqlQuery, gqlMutation } from 'app/lib/graphqlClient'
import { debug, logError } from 'lib/logging'
import type { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'

const WORKSPACE_METRICS_QUERY = graphql(`
  query WorkspaceMetrics($dateRange: DateRangeInput!, $groupBy: String!, $channel: String) {
    marketingMetrics(dateRange: $dateRange, groupBy: $groupBy, channel: $channel) {
      rows {
        bucket
        workspaceId
        leads
        MQLs
        appointments
        patients
        scheduleRate
        cpls
        roi
        budget
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        iCPL
        iCPQL
      }
    }
  }
`)

const CREATE_WORKSPACE_MUTATION = graphql(`
  mutation CreateWorkspace($input: CreateWorkspaceInput!) {
    createWorkspace(input: $input) {
      id
      name
      orgId
    }
  }
`)

export type UseWorkspacesReturn = {
  columns: ColumnDef<MetricsRow, any>[]
  filterableColumns: FilterableColumn[]
  filteredRows: MetricsRow[] | undefined
  totalItems: number
  canCreate: boolean
  open: boolean
  saving: boolean
  setOpen: (open: boolean) => void
  setSaving: (saving: boolean) => void
  onSearchValueChange: (val: string | undefined) => void
  onCreateWorkspace: (name: string) => Promise<void>
}

export function useWorkspaces(): UseWorkspacesReturn {
  const [saving, setSaving] = useState(false)
  const [metrics, setMetrics] = useState<{ hits: MetricsRow[] }>()
  const user = useUser()
  const [searchParams] = useSearchParams()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const checkPermission = useCheckPermission()
  const [searchValue, setSearchValue] = useState<string>()

  // Compute date range from search params
  const channel = isSMCOrg(user?.orgId) ? `Digital Marketing` : ``
  let fromDate = moment().startOf(`month`).format(`YYYY-MM-DD`)
  let toDate = moment().endOf(`month`).format(`YYYY-MM-DD`)
  let dateValue: string | string[] = searchParams.get(`date`) ?? `this_month`
  if (searchParams.has(`date`)) {
    const dateFilter: string[] = searchParams.getAll(`date`)!
    if (dateFilter.length > 1) {
      fromDate = dateFilter[0]
      toDate = dateFilter[1]
      dateValue = [fromDate, toDate]
    } else {
      const dates = getDateRangeValues(DateRangeOperator[dateFilter[0] as string])
      if (dates) {
        fromDate = dates[0].format(`YYYY-MM-DD`)
        toDate = dates[1].format(`YYYY-MM-DD`)
      }
    }
  }

  // Fetch metrics and workspace names
  useEffect(() => {
    const dateRange = { fromDate, toDate }
    Promise.all([
      gqlQuery<{ marketingMetrics: { rows: MetricsRow[] } }>(WORKSPACE_METRICS_QUERY, {
        dateRange,
        groupBy: 'workspaces',
        channel,
      }),
      gqlFetchList<{ id: string; name: string; orgTags?: string[] }>('workspaces', {}, 'id name orgTags'),
    ])
      .then(([metricsResult, workspacesResult]) => {
        const workspaceMap = new Map(workspacesResult.hits.map((ws) => [ws.id, ws]))
        const rows = (metricsResult.data?.marketingMetrics?.rows ?? []).map((row) => {
          const ws = workspaceMap.get(row.bucket)
          return {
            ...row,
            workspace: { name: ws?.name ?? row.bucket, orgTags: ws?.orgTags ?? [] },
          }
        })
        const data = { hits: rows }
        debug(`defer`, `deferredData`, data)
        setMetrics(data)
      })
      .catch((err) => logError(`Error fetching workspace metrics`, err))
  }, [searchParams.toString()])

  const legacyShowOrgAdminColumns = hasRole(user!, `orgAdmin`)
  const permissionShowOrgAdminColumns = checkPermission([`org:marketing:see-spend`, `marketing:spend`])
  const showOrgAdminColumns = staffV2Enabled ? permissionShowOrgAdminColumns : legacyShowOrgAdminColumns

  const safeFilterableColumns = useMemo(
    () =>
      filterableColumns(showOrgAdminColumns, {
        [`date`]: { value: dateValue, operator: `between` },
      }),
    [user, showOrgAdminColumns],
  )

  const filteredRows = useClientSideTableFilters(
    (metrics?.hits ?? []) as MetricsRow[],
    safeFilterableColumns,
    `bucket`,
    [`workspace.name`],
    searchValue,
  )

  // While metrics are still loading, return undefined so the table shows a spinner
  // instead of "No data found" for an empty array.
  const resolvedRows = metrics ? filteredRows : undefined

  const [open, setOpen] = useState(false)

  const columns = useMemo(
    () =>
      defaultColumns(
        showOrgAdminColumns,
        isSMCOrg(user?.orgId),
        fromDate ?? undefined,
        toDate ?? undefined,
        channel ?? `Digital Marketing`,
      ),
    [searchParams, user, showOrgAdminColumns],
  )

  const canCreate = checkPermission([`org:create:workspace`])

  const onCreateWorkspace = useCallback(async (name: string) => {
    const result = await gqlMutation(CREATE_WORKSPACE_MUTATION, { input: { name } })
    if (result.error) {
      notifyError(`Failed to create workspace`)
      logError(`Error creating workspace`, result.error)
      return
    }
    notifySuccess(`Workspace "${name}" created`)
    setOpen(false)

    // Optimistically add the new workspace to the table so it appears immediately.
    // New workspaces have no metrics yet, so the metrics query won't return them.
    const newWorkspace = result.data?.createWorkspace
    if (newWorkspace) {
      setMetrics((prev) => {
        const emptyRow = {
          bucket: newWorkspace.id,
          workspaceId: newWorkspace.id,
          workspace: { name: newWorkspace.name },
          leads: 0,
          MQLs: 0,
          appointments: 0,
          patients: 0,
          scheduleRate: 0,
          cpls: 0,
          roi: 0,
          budget: 0,
          facebookSpend: 0,
          googleSpend: 0,
          targetSpend: 0,
          leadGoal: 0,
          iCPL: 0,
          iCPQL: 0,
        } as MetricsRow
        const hits = [...(prev?.hits ?? []), emptyRow]
        hits.sort((a, b) => {
          const nameA = (a as MetricsRow & { workspace: { name: string } }).workspace?.name ?? ''
          const nameB = (b as MetricsRow & { workspace: { name: string } }).workspace?.name ?? ''
          return nameA.localeCompare(nameB)
        })
        return { hits }
      })
    }
  }, [])

  return {
    columns,
    filterableColumns: safeFilterableColumns,
    filteredRows: resolvedRows as MetricsRow[] | undefined,
    totalItems: metrics?.hits?.length || 0,
    canCreate,
    open,
    saving,
    setOpen,
    setSaving,
    onSearchValueChange: setSearchValue,
    onCreateWorkspace,
  }
}
